import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  ScrollView,
  TouchableOpacity,
  Alert
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { VerificationResult, ImportType, Discrepancy } from '../types/importVerification';

interface ImportVerificationModalProps {
  visible: boolean;
  onClose: () => void;
  verificationResult: VerificationResult | null;
  importType: ImportType;
  fileName: string;
  onAcceptResults: () => void;
  onRejectResults: () => void;
}

export const ImportVerificationModal: React.FC<ImportVerificationModalProps> = ({
  visible,
  onClose,
  verificationResult,
  importType,
  fileName,
  onAcceptResults,
  onRejectResults
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  if (!verificationResult) return null;

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const formatCurrency = (amount: number) => {
    return `AED ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getStatusColor = (passed: boolean) => passed ? '#10B981' : '#EF4444';
  const getStatusIcon = (passed: boolean) => passed ? 'checkmark-circle' : 'alert-circle';

  const handleAccept = () => {
    if (!verificationResult.passed) {
      Alert.alert(
        'Verification Failed',
        'The import verification failed. Are you sure you want to accept these results?',
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Accept Anyway', style: 'destructive', onPress: onAcceptResults }
        ]
      );
    } else {
      onAcceptResults();
    }
  };

  const handleReject = () => {
    Alert.alert(
      'Reject Import',
      'This will rollback the import and remove all imported data. Are you sure?',
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Reject Import', style: 'destructive', onPress: onRejectResults }
      ]
    );
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return '#EF4444';
      case 'warning': return '#F59E0B';
      case 'info': return '#3B82F6';
      default: return '#6B7280';
    }
  };

  return (
    <Modal
      animationType="slide"
      transparent={true}
      visible={visible}
      onRequestClose={onClose}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          {/* Header */}
          <View style={styles.modalHeader}>
            <View style={styles.headerLeft}>
              <Ionicons 
                name={getStatusIcon(verificationResult.passed)} 
                size={24} 
                color={getStatusColor(verificationResult.passed)} 
              />
              <Text style={styles.modalTitle}>Import Verification</Text>
            </View>
            <TouchableOpacity style={styles.closeButton} onPress={onClose}>
              <Ionicons name="close" size={24} color="#666" />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.modalBody} showsVerticalScrollIndicator={false}>
            {/* Import Info */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Import Details</Text>
              <View style={styles.infoCard}>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Type:</Text>
                  <Text style={styles.infoValue}>{importType.toUpperCase()}</Text>
                </View>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>File:</Text>
                  <Text style={styles.infoValue} numberOfLines={1}>{fileName}</Text>
                </View>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Status:</Text>
                  <View style={styles.statusContainer}>
                    <Ionicons 
                      name={getStatusIcon(verificationResult.passed)} 
                      size={16} 
                      color={getStatusColor(verificationResult.passed)} 
                    />
                    <Text style={[styles.statusText, { color: getStatusColor(verificationResult.passed) }]}>
                      {verificationResult.passed ? 'VERIFIED' : 'FAILED'}
                    </Text>
                  </View>
                </View>
              </View>
            </View>

            {/* Summary */}
            <View style={styles.section}>
              <TouchableOpacity 
                style={styles.expandableHeader}
                onPress={() => toggleSection('summary')}
              >
                <Text style={styles.sectionTitle}>Verification Summary</Text>
                <Ionicons 
                  name={expandedSections.has('summary') ? 'chevron-down' : 'chevron-forward'} 
                  size={20} 
                  color="#666" 
                />
              </TouchableOpacity>
              
              {expandedSections.has('summary') && (
                <View style={styles.summaryCard}>
                  <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>Records Processed:</Text>
                    <Text style={styles.summaryValue}>{verificationResult.summary.csvRecordsProcessed}</Text>
                  </View>
                  <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>Records Imported:</Text>
                    <Text style={[styles.summaryValue, { color: '#10B981' }]}>
                      {verificationResult.summary.recordsImported}
                    </Text>
                  </View>
                  <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>Duplicates Found:</Text>
                    <Text style={[styles.summaryValue, { color: '#F59E0B' }]}>
                      {verificationResult.summary.duplicatesFound}
                    </Text>
                  </View>
                  <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>Records Rejected:</Text>
                    <Text style={[styles.summaryValue, { color: '#EF4444' }]}>
                      {verificationResult.summary.recordsRejected}
                    </Text>
                  </View>
                  
                  <View style={styles.divider} />
                  
                  <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>Expected Amount Increase:</Text>
                    <Text style={styles.summaryValue}>
                      {formatCurrency(verificationResult.summary.expectedAmountIncrease)}
                    </Text>
                  </View>
                  <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>Actual Amount Increase:</Text>
                    <Text style={[
                      styles.summaryValue, 
                      { color: verificationResult.summary.amountVerificationPassed ? '#10B981' : '#EF4444' }
                    ]}>
                      {formatCurrency(verificationResult.summary.actualAmountIncrease)}
                    </Text>
                  </View>
                  
                  {verificationResult.summary.amountDiscrepancy > 0 && (
                    <View style={styles.summaryRow}>
                      <Text style={styles.summaryLabel}>Amount Discrepancy:</Text>
                      <Text style={[styles.summaryValue, { color: '#EF4444' }]}>
                        {formatCurrency(verificationResult.summary.amountDiscrepancy)}
                      </Text>
                    </View>
                  )}
                </View>
              )}
            </View>

            {/* Discrepancies */}
            {verificationResult.discrepancies.length > 0 && (
              <View style={styles.section}>
                <TouchableOpacity 
                  style={styles.expandableHeader}
                  onPress={() => toggleSection('discrepancies')}
                >
                  <Text style={[styles.sectionTitle, { color: '#EF4444' }]}>
                    Discrepancies ({verificationResult.discrepancies.length})
                  </Text>
                  <Ionicons 
                    name={expandedSections.has('discrepancies') ? 'chevron-down' : 'chevron-forward'} 
                    size={20} 
                    color="#EF4444" 
                  />
                </TouchableOpacity>
                
                {expandedSections.has('discrepancies') && (
                  <View style={styles.discrepanciesContainer}>
                    {verificationResult.discrepancies.map((discrepancy, index) => (
                      <View key={index} style={styles.discrepancyCard}>
                        <View style={styles.discrepancyHeader}>
                          <Ionicons 
                            name="warning" 
                            size={16} 
                            color={getSeverityColor(discrepancy.severity)} 
                          />
                          <Text style={[styles.discrepancyTitle, { color: getSeverityColor(discrepancy.severity) }]}>
                            {discrepancy.type.toUpperCase()} - {discrepancy.severity.toUpperCase()}
                          </Text>
                        </View>
                        <Text style={styles.discrepancyDescription}>
                          {discrepancy.description}
                        </Text>
                        {discrepancy.suggestions.length > 0 && (
                          <View style={styles.suggestionsContainer}>
                            <Text style={styles.suggestionsTitle}>Suggestions:</Text>
                            {discrepancy.suggestions.map((suggestion, idx) => (
                              <Text key={idx} style={styles.suggestionText}>
                                • {suggestion}
                              </Text>
                            ))}
                          </View>
                        )}
                      </View>
                    ))}
                  </View>
                )}
              </View>
            )}

            {/* Duplicates */}
            {verificationResult.duplicateLog.length > 0 && (
              <View style={styles.section}>
                <TouchableOpacity 
                  style={styles.expandableHeader}
                  onPress={() => toggleSection('duplicates')}
                >
                  <Text style={styles.sectionTitle}>
                    Duplicate Records ({verificationResult.duplicateLog.length})
                  </Text>
                  <Ionicons 
                    name={expandedSections.has('duplicates') ? 'chevron-down' : 'chevron-forward'} 
                    size={20} 
                    color="#666" 
                  />
                </TouchableOpacity>
                
                {expandedSections.has('duplicates') && (
                  <View style={styles.duplicatesContainer}>
                    {verificationResult.duplicateLog.slice(0, 10).map((duplicate, index) => (
                      <View key={index} style={styles.duplicateCard}>
                        <Text style={styles.duplicateRow}>Row {duplicate.csvRowNumber}</Text>
                        <Text style={styles.duplicateReason}>{duplicate.rejectionReason}</Text>
                      </View>
                    ))}
                    {verificationResult.duplicateLog.length > 10 && (
                      <Text style={styles.moreRecordsText}>
                        And {verificationResult.duplicateLog.length - 10} more duplicates...
                      </Text>
                    )}
                  </View>
                )}
              </View>
            )}
          </ScrollView>

          {/* Footer Actions */}
          <View style={styles.modalFooter}>
            <TouchableOpacity 
              style={[styles.actionButton, styles.rejectButton]} 
              onPress={handleReject}
            >
              <Ionicons name="close-circle" size={20} color="#fff" />
              <Text style={styles.rejectButtonText}>Reject Import</Text>
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={[styles.actionButton, styles.acceptButton]} 
              onPress={handleAccept}
            >
              <Ionicons name="checkmark-circle" size={20} color="#fff" />
              <Text style={styles.acceptButtonText}>Accept Results</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    maxHeight: '90%',
    width: '100%',
    maxWidth: 600,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
  },
  closeButton: {
    padding: 4,
  },
  modalBody: {
    flex: 1,
    padding: 20,
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 12,
  },
  expandableHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  infoCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 16,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  infoLabel: {
    fontSize: 14,
    color: '#666',
    flex: 1,
  },
  infoValue: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
    flex: 2,
    textAlign: 'right',
  },
  statusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  statusText: {
    fontSize: 14,
    fontWeight: '600',
  },
  summaryCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 16,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  summaryLabel: {
    fontSize: 14,
    color: '#666',
    flex: 1,
  },
  summaryValue: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
  },
  divider: {
    height: 1,
    backgroundColor: '#e0e0e0',
    marginVertical: 12,
  },
  discrepanciesContainer: {
    gap: 12,
  },
  discrepancyCard: {
    backgroundColor: '#fff5f5',
    borderRadius: 8,
    padding: 16,
    borderLeftWidth: 4,
    borderLeftColor: '#EF4444',
  },
  discrepancyHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  discrepancyTitle: {
    fontSize: 14,
    fontWeight: '600',
  },
  discrepancyDescription: {
    fontSize: 14,
    color: '#333',
    marginBottom: 8,
    lineHeight: 20,
  },
  suggestionsContainer: {
    marginTop: 8,
  },
  suggestionsTitle: {
    fontSize: 13,
    fontWeight: '500',
    color: '#666',
    marginBottom: 4,
  },
  suggestionText: {
    fontSize: 13,
    color: '#666',
    marginLeft: 8,
    lineHeight: 18,
  },
  duplicatesContainer: {
    gap: 8,
  },
  duplicateCard: {
    backgroundColor: '#fffbeb',
    borderRadius: 8,
    padding: 12,
    borderLeftWidth: 3,
    borderLeftColor: '#F59E0B',
  },
  duplicateRow: {
    fontSize: 13,
    fontWeight: '500',
    color: '#333',
    marginBottom: 4,
  },
  duplicateReason: {
    fontSize: 12,
    color: '#666',
  },
  moreRecordsText: {
    fontSize: 13,
    color: '#666',
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: 8,
  },
  modalFooter: {
    flexDirection: 'row',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
    gap: 12,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    gap: 8,
  },
  rejectButton: {
    backgroundColor: '#EF4444',
  },
  acceptButton: {
    backgroundColor: '#10B981',
  },
  rejectButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  acceptButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
});
